import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Grand Opening: 1 September 2026, 08:00:00 Malaysia Time (UTC+8)
const TARGET_DATE = new Date('2026-09-01T08:00:00+08:00').getTime();
const TOTAL_DAYS = 365; // ring reference for days

const getTimeLeft = () => {
  const now = Date.now();
  const diff = TARGET_DATE - now;
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, total: 0 };
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
    total: diff,
  };
};

const getMilestoneText = (days) => {
  if (days <= 0) return 'Now Open';
  if (days === 1) return 'Opening Tomorrow';
  if (days <= 7) return 'Opening This Week';
  return 'Opening Soon';
};

const CircleRing = ({ value, max, label, displayLabel, strokeWidth = 3 }) => {
  const size = typeof window !== 'undefined' && window.innerWidth < 400 ? 64 : 80;
  const radius = (size - strokeWidth * 2) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = value / max;
  const offset = circumference * (1 - progress);
  const isSeconds = label === 'sec';
  const formatted = String(value).padStart(label === 'days' ? 1 : 2, '0');

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative" style={{ width: size, height: size }}>
        {/* Background circle */}
        <svg
          width={size}
          height={size}
          className="absolute inset-0"
          style={{ transform: 'rotate(-90deg)' }}
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="rgba(214,168,74,0.08)"
            strokeWidth={strokeWidth}
          />
          {/* Progress arc */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="url(#goldGradient)"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            style={{
              transition: isSeconds ? 'stroke-dashoffset 0.8s ease' : 'stroke-dashoffset 1.2s ease',
            }}
          />
          <defs>
            <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#e8c36a" />
              <stop offset="100%" stopColor="#c9972f" />
            </linearGradient>
          </defs>
        </svg>

        {/* Inner glow for seconds */}
        {isSeconds && (
          <div
            className="absolute inset-0 rounded-full"
            style={{
              boxShadow: 'inset 0 0 12px rgba(214,168,74,0.06)',
            }}
          />
        )}

        {/* Value */}
        <div className="absolute inset-0 flex items-center justify-center">
          <span
            className="text-xl sm:text-2xl font-bold tabular-nums"
            style={{
              color: 'var(--vc-gold)',
              fontFamily: 'Inter, sans-serif',
              letterSpacing: '-0.02em',
            }}
          >
            {formatted}
          </span>
        </div>
      </div>

      <span
        className="text-[10px] sm:text-xs font-medium uppercase tracking-[0.12em]"
        style={{
          color: 'var(--vc-fg-subtle)',
          fontFamily: 'Inter, sans-serif',
        }}
      >
        {displayLabel}
      </span>
    </div>
  );
};

export const CountdownTimer = () => {
  const { t } = useTranslation();
  const [time, setTime] = useState(getTimeLeft);

  useEffect(() => {
    const interval = setInterval(() => {
      setTime(getTimeLeft());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const milestoneText = getMilestoneText(time.days);
  const isOpen = time.total <= 0;

  if (isOpen) {
    return (
      <div
        data-testid="countdown-open"
        className="mt-8 inline-flex items-center gap-3 px-6 py-3 rounded-2xl"
        style={{
          backgroundColor: 'rgba(214,168,74,0.1)',
          border: '1px solid rgba(214,168,74,0.25)',
        }}
      >
        <div
          className="w-2.5 h-2.5 rounded-full animate-pulse"
          style={{ backgroundColor: 'var(--vc-gold)' }}
        />
        <span
          className="text-sm font-semibold tracking-wide"
          style={{ color: 'var(--vc-gold)', fontFamily: 'Inter, sans-serif' }}
        >
          {t('countdown.now_open')}
        </span>
        <span className="mx-1" style={{ color: 'rgba(214,168,74,0.3)' }}>|</span>
        <span
          className="text-sm"
          style={{ color: 'var(--vc-fg-muted)', fontFamily: 'Inter, sans-serif' }}
        >
          {t('countdown.book')}
        </span>
      </div>
    );
  }

  return (
    <div data-testid="countdown-timer" className="mt-8 flex flex-col items-center">
      {/* Milestone label */}
      <div
        className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full mb-5"
        style={{
          backgroundColor: 'rgba(214,168,74,0.08)',
          border: '1px solid rgba(214,168,74,0.2)',
        }}
      >
        <span
          className="relative flex h-2.5 w-2.5"
        >
          <span
            className="animate-ping absolute inset-0 rounded-full"
            style={{ backgroundColor: 'rgba(214,168,74,0.6)' }}
          />
          <span
            className="relative inline-flex rounded-full h-2.5 w-2.5"
            style={{ backgroundColor: 'var(--vc-gold)' }}
          />
        </span>
        <span
          className="text-xs font-semibold uppercase tracking-[0.12em]"
          style={{ color: 'var(--vc-gold)', fontFamily: 'Inter, sans-serif' }}
        >
          {t('hero.opening_soon')}
        </span>
      </div>

      {/* Circle rings */}
      <div className="flex items-start gap-4 sm:gap-6">
        <CircleRing value={time.days} max={TOTAL_DAYS} label="days" displayLabel={t('countdown.days')} strokeWidth={3} />
        <CircleRing value={time.hours} max={24} label="hours" displayLabel={t('countdown.hours')} strokeWidth={3} />
        <CircleRing value={time.minutes} max={60} label="min" displayLabel={t('countdown.min')} strokeWidth={3} />
        <CircleRing value={time.seconds} max={60} label="sec" displayLabel={t('countdown.sec')} strokeWidth={3} />
      </div>
    </div>
  );
};
